<template>
    <div class="flex flex-col h-full">
        <!-- Status bar (always visible) -->
        <div class="px-4 py-2 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
            <template v-if="generating && !generatedEmail">
                <span class="w-2 h-2 rounded-full bg-yellow-400 animate-pulse inline-block"></span>
                <span class="text-xs text-amber-600">Generating...</span>
            </template>
            <template v-else-if="generatedEmail">
                <span class="w-2 h-2 rounded-full bg-green-400 inline-block"></span>
                <span class="text-xs text-emerald-600">Generated</span>
                <span v-if="modelName" class="text-xs text-gray-500">via {{ modelName }}</span>
                <span v-if="generationTime" class="text-xs text-gray-500">
                    in {{ Number(generationTime).toFixed(1) }}s
                </span>
            </template>
            <template v-else>
                <span class="text-xs text-gray-500">Select a record and purpose to generate</span>
            </template>
        </div>

        <!-- Main content area -->
        <div class="flex-1 overflow-y-auto">
            <!-- Empty state (no email, not generating) -->
            <div
                v-if="!generatedEmail && !generating"
                class="flex flex-col items-center justify-center h-full text-center px-8 py-16"
            >
                <i class="fa-duotone fa-solid fa-envelope text-5xl text-gray-300 mb-4"></i>
                <p class="text-sm text-gray-500 mb-2">Your email preview will appear here</p>
                <p class="text-xs text-gray-400">Select a record and choose a purpose to get started</p>
            </div>

            <!-- Generating skeleton (only when no partial email yet) -->
            <div
                v-else-if="generating && !generatedEmail"
                class="px-4 py-6"
            >
                <div class="max-w-xl mx-auto bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
                    <!-- Skeleton header -->
                    <div class="px-5 py-4 border-b border-gray-200 space-y-3">
                        <div class="flex items-center gap-2">
                            <div class="w-6 h-2 bg-gray-200 rounded"></div>
                            <div class="w-40 h-2 bg-gray-200 rounded"></div>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="w-12 h-2 bg-gray-200 rounded"></div>
                            <div class="w-56 h-3 bg-gray-600 rounded"></div>
                        </div>
                    </div>
                    <!-- Skeleton body -->
                    <div class="px-5 py-4 space-y-3">
                        <div class="w-full h-2 bg-gray-200 rounded"></div>
                        <div class="w-5/6 h-2 bg-gray-200 rounded"></div>
                        <div class="w-full h-2 bg-gray-200 rounded"></div>
                        <div class="w-4/6 h-2 bg-gray-200 rounded"></div>
                        <div class="w-full h-2 bg-gray-200 rounded"></div>
                        <div class="w-3/4 h-2 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>

            <!-- Email preview card (email exists — including streaming) -->
            <div
                v-else-if="generatedEmail"
                class="px-4 py-6"
            >
                <div class="max-w-xl mx-auto bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <!-- Email header -->
                    <div class="px-5 py-4 border-b border-gray-200 space-y-2">
                        <!-- To field -->
                        <div class="flex items-baseline gap-2">
                            <span class="text-gray-500 flex-shrink-0" style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em;">To</span>
                            <span class="text-gray-700 text-xs truncate">
                                <span v-if="selectedRecord">
                                    {{ selectedRecord.name }}<span v-if="selectedRecord.email"> &lt;{{ selectedRecord.email }}&gt;</span>
                                </span>
                                <span v-else class="text-gray-400 italic">No record selected</span>
                            </span>
                        </div>
                        <!-- Subject field -->
                        <div class="flex items-baseline gap-2">
                            <span class="text-gray-500 flex-shrink-0" style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em;">Subject</span>
                            <span class="text-gray-900 font-semibold text-sm">{{ generatedEmail.subject }}</span>
                        </div>
                    </div>

                    <!-- Email body -->
                    <div class="px-5 py-4">
                        <p
                            class="text-gray-500 whitespace-pre-wrap leading-relaxed"
                            style="font-size: 13px;"
                        >{{ generatedEmail.body }}</p>
                    </div>
                </div>

                <!-- Send result banner -->
                <div
                    v-if="sendResult"
                    class="max-w-xl mx-auto mt-4 rounded-lg px-4 py-3 text-sm"
                    :class="sendResult.success
                        ? 'bg-green-900/50 border border-green-700 text-emerald-600'
                        : 'bg-red-900/50 border border-red-700 text-red-300'"
                >
                    <span>{{ sendResult.message }}</span>
                    <a
                        v-if="sendResult.success && sendResult.salesforce_link"
                        :href="sendResult.salesforce_link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ml-2 underline hover:opacity-80 transition-opacity"
                    >View in Salesforce <i class="fa-solid fa-arrow-up-right-from-square text-xs ml-1"></i></a>
                </div>
            </div>
        </div>

        <!-- Action bar (visible when email exists) -->
        <div
            v-if="generatedEmail"
            class="px-4 py-3 border-t border-gray-200 flex items-center justify-between flex-shrink-0"
        >
            <!-- Left: Regenerate + Edit -->
            <div class="flex items-center gap-2">
                <button
                    class="px-3 py-1.5 rounded-md bg-white border border-gray-200 text-gray-700 hover:border-gray-500 hover:text-gray-900 transition-colors text-xs"
                    @click="$emit('regenerate')"
                >
                    <i class="fa-solid fa-arrows-rotate text-xs mr-1"></i> Regenerate
                </button>
                <button
                    class="px-3 py-1.5 rounded-md bg-white border border-gray-200 text-gray-700 hover:border-gray-500 hover:text-gray-900 transition-colors text-xs"
                    @click="onEdit"
                >
                    <i class="fa-solid fa-pen text-xs mr-1"></i> Edit
                </button>
            </div>

            <!-- Right: Send (disabled in preview-only mode) -->
            <button
                disabled
                class="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all flex items-center gap-2 bg-gray-300 cursor-not-allowed opacity-60"
                title="Sending is disabled in preview mode"
            >
                <i class="fa-solid fa-paper-plane"></i>
                <span>Send disabled</span>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EmailPreview',

    props: {
        generatedEmail: {
            type: Object,
            default: null,
        },
        generating: {
            type: Boolean,
            default: false,
        },
        modelName: {
            type: String,
            default: '',
        },
        generationTime: {
            type: [String, Number],
            default: null,
        },
        selectedRecord: {
            type: Object,
            default: null,
        },
        sending: {
            type: Boolean,
            default: false,
        },
        sendResult: {
            type: Object,
            default: null,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['regenerate', 'send', 'edit', 'update:editMode', 'update:generatedEmail'],

    methods: {
        confirmSend() {
            if (!this.generatedEmail || this.sending) return

            const name = this.selectedRecord?.name ?? 'this recipient'
            const email = this.selectedRecord?.email ?? ''
            const message = email
                ? `Send this email to ${name} at ${email}?`
                : `Send this email to ${name}?`

            if (window.confirm(message)) {
                this.$emit('send')
            }
        },

        onEdit() {
            this.$emit('edit')
            this.$emit('update:editMode', true)
        },
    },
}
</script>
